/**
 * RTL (Right-to-Left) Utilities
 *
 * Provides helper functions for creating RTL-aware styles and layouts.
 * Use these utilities to ensure proper display in both Arabic (RTL) and English (LTR).
 */

import { ViewStyle, TextStyle, FlexStyle } from 'react-native';

/**
 * Get text alignment based on RTL direction
 */
export const getTextAlign = (isRTL: boolean): 'left' | 'right' => {
  return isRTL ? 'right' : 'left';
};

/**
 * Get opposite text alignment (for special cases)
 */
export const getOppositeTextAlign = (isRTL: boolean): 'left' | 'right' => {
  return isRTL ? 'left' : 'right';
};

/**
 * Get flex direction for horizontal layouts
 */
export const getFlexRow = (isRTL: boolean): 'row' | 'row-reverse' => {
  return isRTL ? 'row-reverse' : 'row';
};

/**
 * Get flex direction that keeps consistent visual order regardless of language
 * Use this when you want items to appear in the same visual position in both RTL and LTR
 */
export const getConsistentRow = (): 'row-reverse' => {
  return 'row-reverse';
};

/**
 * Get start alignment based on RTL direction
 */
export const getStart = (isRTL: boolean): 'flex-start' | 'flex-end' => {
  return isRTL ? 'flex-end' : 'flex-start';
};

/**
 * Get end alignment based on RTL direction
 */
export const getEnd = (isRTL: boolean): 'flex-start' | 'flex-end' => {
  return isRTL ? 'flex-start' : 'flex-end';
};

/**
 * Create margin start style
 */
export const marginStart = (value: number, isRTL: boolean): ViewStyle => {
  return isRTL ? { marginRight: value } : { marginLeft: value };
};

/**
 * Create margin end style
 */
export const marginEnd = (value: number, isRTL: boolean): ViewStyle => {
  return isRTL ? { marginLeft: value } : { marginRight: value };
};

/**
 * Create padding start style
 */
export const paddingStart = (value: number, isRTL: boolean): ViewStyle => {
  return isRTL ? { paddingRight: value } : { paddingLeft: value };
};

/**
 * Create padding end style
 */
export const paddingEnd = (value: number, isRTL: boolean): ViewStyle => {
  return isRTL ? { paddingLeft: value } : { paddingRight: value };
};

/**
 * Create border start style
 */
export const borderStart = (width: number, color: string, isRTL: boolean): ViewStyle => {
  return isRTL
    ? { borderRightWidth: width, borderRightColor: color }
    : { borderLeftWidth: width, borderLeftColor: color };
};

/**
 * Create border end style
 */
export const borderEnd = (width: number, color: string, isRTL: boolean): ViewStyle => {
  return isRTL
    ? { borderLeftWidth: width, borderLeftColor: color }
    : { borderRightWidth: width, borderRightColor: color };
};

/**
 * Get absolute position for start side
 */
export const absoluteStart = (value: number, isRTL: boolean): ViewStyle => {
  return isRTL ? { right: value } : { left: value };
};

/**
 * Get absolute position for end side
 */
export const absoluteEnd = (value: number, isRTL: boolean): ViewStyle => {
  return isRTL ? { left: value } : { right: value };
};

/**
 * Flip a transform rotation for RTL
 * Useful for chevron/arrow icons
 */
export const flipForRTL = (isRTL: boolean): ViewStyle => {
  return isRTL ? { transform: [{ scaleX: -1 }] } : {};
};

/**
 * Get icon rotation for navigation arrows
 * Left arrow: 180deg in LTR, 0deg in RTL
 * Right arrow: 0deg in LTR, 180deg in RTL
 */
export const getArrowRotation = (direction: 'left' | 'right', isRTL: boolean): string => {
  if (direction === 'left') {
    return isRTL ? '0deg' : '180deg';
  }
  return isRTL ? '180deg' : '0deg';
};

/**
 * Create a complete RTL-aware style object
 * Handles text alignment, flex direction, and margins/paddings
 */
export interface RTLStyleOptions {
  textAlign?: boolean;
  flexRow?: boolean;
  marginStart?: number;
  marginEnd?: number;
  paddingStart?: number;
  paddingEnd?: number;
}

export const createRTLStyle = (options: RTLStyleOptions, isRTL: boolean): ViewStyle & TextStyle => {
  const style: ViewStyle & TextStyle = {};

  if (options.textAlign) {
    style.textAlign = getTextAlign(isRTL);
  }

  if (options.flexRow) {
    style.flexDirection = getFlexRow(isRTL);
  }

  if (options.marginStart !== undefined) {
    Object.assign(style, marginStart(options.marginStart, isRTL));
  }

  if (options.marginEnd !== undefined) {
    Object.assign(style, marginEnd(options.marginEnd, isRTL));
  }

  if (options.paddingStart !== undefined) {
    Object.assign(style, paddingStart(options.paddingStart, isRTL));
  }

  if (options.paddingEnd !== undefined) {
    Object.assign(style, paddingEnd(options.paddingEnd, isRTL));
  }

  return style;
};

/**
 * Hook-style function to get all RTL utilities for a given direction
 */
export const getRTLUtils = (isRTL: boolean) => ({
  textAlign: getTextAlign(isRTL),
  oppositeTextAlign: getOppositeTextAlign(isRTL),
  flexRow: getFlexRow(isRTL),
  start: getStart(isRTL),
  end: getEnd(isRTL),
  marginStart: (value: number) => marginStart(value, isRTL),
  marginEnd: (value: number) => marginEnd(value, isRTL),
  paddingStart: (value: number) => paddingStart(value, isRTL),
  paddingEnd: (value: number) => paddingEnd(value, isRTL),
  borderStart: (width: number, color: string) => borderStart(width, color, isRTL),
  borderEnd: (width: number, color: string) => borderEnd(width, color, isRTL),
  absoluteStart: (value: number) => absoluteStart(value, isRTL),
  absoluteEnd: (value: number) => absoluteEnd(value, isRTL),
  flip: flipForRTL(isRTL),
  isRTL,
});

/**
 * Convert Arabic-Indic numerals (٠١٢٣٤٥٦٧٨٩) to Western digits (0123456789).
 * Preserves all non-digit characters (dots, minus, etc.).
 */
export const normalizeArabicNumbers = (text: string): string => {
  return text.replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660));
};

export default {
  getTextAlign,
  getOppositeTextAlign,
  getFlexRow,
  getConsistentRow,
  getStart,
  getEnd,
  marginStart,
  marginEnd,
  paddingStart,
  paddingEnd,
  borderStart,
  borderEnd,
  absoluteStart,
  absoluteEnd,
  flipForRTL,
  getArrowRotation,
  createRTLStyle,
  getRTLUtils,
};
